import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { segmentationBuilderApi } from './segmentation-api';

interface SegmentationConfigureProps {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.SegmentationConfigure;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 750px;
  margin: 0 auto;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
  margin: 0;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e5e5;
`;

const CardBody = styled.div`
  padding: 16px;
`;

const BrandGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`;

const BrandCard = styled.div<{ $selected: boolean }>`
  padding: 12px;
  border-radius: 8px;
  border: 2px solid ${(p) => (p.$selected ? '#28AAE1' : '#e5e5e5')};
  background: ${(p) => (p.$selected ? '#E5F5FC' : '#ffffff')};
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    border-color: #28aae1;
    transform: translateY(-1px);
  }
`;

const BrandName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
  margin-bottom: 4px;
`;

const BrandStats = styled.div`
  font-size: 12px;
  color: #626262;
`;

const VolumeCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const VolumeLabel = styled.div`
  flex: 1;
  font-size: 14px;
  color: #323232;
`;

const VolumeInput = styled.input`
  width: 80px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 8px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #323232;
  outline: none;
  &:focus {
    border-color: #28aae1;
  }
`;

const ConfigureButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #28aae1;
  color: #ffffff;
  transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(40, 170, 225, 0.3);
  &:hover:not(:disabled) {
    background: #1d94c7;
    transform: translateY(-1px);
  }
  &:disabled {
    background: #e5e5e5;
    color: #adadad;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ErrorBox = styled.div`
  background: #fde5e5;
  border: 1px solid #e12e40;
  border-radius: 8px;
  padding: 10px 14px;
  color: #c62828;
  font-size: 14px;
`;

export function SegmentationConfigure({ queryId, queryIdentifier, value }: SegmentationConfigureProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [volumeFilter, setVolumeFilter] = useState(5);
  const [periodSplit] = useState('2023-06-01');
  const [regimenCoverage] = useState(80);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Initialize with saved config if available
    if (value.brandConfig) {
      const config = value.brandConfig as { focal_brand_ids?: string[]; volume_filter?: number };
      if (config.focal_brand_ids) {
        setSelected(new Set(config.focal_brand_ids));
      }
      if (config.volume_filter) {
        setVolumeFilter(config.volume_filter);
      }
    }
  }, [value.brandConfig]);

  const toggleBrand = (brandId: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(brandId) ? next.delete(brandId) : next.add(brandId);
      return next;
    });
  };

  const handleConfigure = async (): Promise<void> => {
    if (selected.size === 0) {
      setError('Please select at least one brand');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await segmentationBuilderApi.submitConfiguration(queryIdentifier, {
        focal_brand_ids: Array.from(selected),
        volume_filter: volumeFilter,
        period_split: periodSplit,
        regimen_coverage_pct: regimenCoverage / 100,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to configure');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <Card>
        <CardHeader>
          <SectionTitle>Brand Selection</SectionTitle>
          <div style={{ fontSize: 12, color: '#626262', marginTop: 4 }}>
            Select the brands/molecules you want to analyze in your segmentation
          </div>
        </CardHeader>
        <CardBody>
          <BrandGrid>
            {value.brands.map((brand) => (
              <BrandCard
                key={`${brand.molecule}-${brand.product_form}`}
                $selected={selected.has(`${brand.molecule}-${brand.product_form}`)}
                onClick={() => toggleBrand(`${brand.molecule}-${brand.product_form}`)}
              >
                <BrandName>{brand.brand_name}</BrandName>
                <BrandStats>
                  {brand.molecule} ({brand.product_form})
                </BrandStats>
                <BrandStats style={{ marginTop: 4 }}>
                  {brand.claims_with_product.toLocaleString()} claims • {brand.patient_count.toLocaleString()} patients
                  • {brand.hcp_count.toLocaleString()} HCPs
                </BrandStats>
              </BrandCard>
            ))}
          </BrandGrid>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <SectionTitle>Volume Filter</SectionTitle>
          <div style={{ fontSize: 12, color: '#626262', marginTop: 4 }}>
            Minimum patients per HCP to include in segmentation
          </div>
        </CardHeader>
        <CardBody>
          <VolumeCard>
            <VolumeLabel>Minimum patients per HCP:</VolumeLabel>
            <VolumeInput
              type="number"
              min="1"
              max="50"
              value={volumeFilter}
              onChange={(e) => setVolumeFilter(parseInt(e.target.value) || 5)}
            />
          </VolumeCard>
        </CardBody>
      </Card>

      {error && <ErrorBox>{error}</ErrorBox>}

      <ConfigureButton type="button" disabled={saving || selected.size === 0} onClick={handleConfigure}>
        {saving ? 'Configuring...' : `Configure Analysis${selected.size > 0 ? ` (${selected.size} brands)` : ''}`}
      </ConfigureButton>
    </Wrapper>
  );
}
